/**
 * Phase 35 Session 35.2a — Help Client transport registrar.
 *
 * Mirrors the Phase 21.1 `setStorageAdapter(impl)` pattern in
 * `@commerce-os/pos-core` — every consumer (dashboard, RN POS, web POS,
 * Management, storefront) calls `setHelpTransport(client)` once at boot
 * with the AxiosInstance that's already authenticated for that surface,
 * and every hook in this package picks it up via `getHelpTransport()`.
 *
 * Why a singleton instead of a React Context provider:
 *   1. The hooks need to be callable from non-React code paths (e.g. a
 *      service-layer prefetch in pos-core or a Zustand action). A
 *      module-level singleton is reachable from both.
 *   2. Each app already has exactly one authenticated AxiosInstance
 *      (RN apps via `@commerce-os/rn-shared`'s `getApiClient()`; web
 *      apps via their own `apps/<app>/src/lib/axios.ts`). There's no
 *      legitimate scenario where two transports coexist in the same
 *      process.
 *   3. The HelpClientProvider exists as ergonomic sugar (calls
 *      `setHelpTransport` for you on mount + ensures React Query is
 *      mounted), but isn't required for the singleton to work.
 *
 * The transport stays null until set; consumers calling a hook before
 * boot get a clear `HelpClientNotConfiguredError` rather than a
 * cryptic "client.get is not a function" runtime error.
 */
import type { AxiosInstance } from 'axios';

let _transport: AxiosInstance | null = null;

/**
 * Register the AxiosInstance the help-client hooks should issue HTTP
 * calls against. Call this exactly once at app boot — before any
 * component using a help hook mounts.
 *
 * Idempotent on the same instance (re-setting with the same reference
 * is a no-op so HMR / fast-refresh doesn't churn). Throws on a real
 * second registration with a different instance — that's almost
 * always a bug worth surfacing loudly.
 */
export function setHelpTransport(client: AxiosInstance): void {
  if (_transport === client) return;
  if (_transport && _transport !== client) {
    throw new HelpClientConfigurationError(
      'setHelpTransport called with a different AxiosInstance. ' +
        'Each app should register exactly one transport at boot. ' +
        'Call resetHelpTransport() first if you legitimately need to swap.',
    );
  }
  _transport = client;
}

/**
 * Read the registered transport. Throws when the transport hasn't been
 * set yet — this is the loud-fail path that surfaces "you forgot to
 * call setHelpTransport at boot" instead of a confusing axios error.
 */
export function getHelpTransport(): AxiosInstance {
  if (!_transport) {
    throw new HelpClientNotConfiguredError(
      'Help client transport not registered. Call setHelpTransport(client) ' +
        'at app boot, or wrap the React tree in <HelpClientProvider transport={client}>.',
    );
  }
  return _transport;
}

/**
 * Test/teardown helper. Resets the singleton so a spec can re-set
 * cleanly. Not exported from the package barrel — consumers shouldn't
 * touch this in production code.
 */
export function resetHelpTransport(): void {
  _transport = null;
}

/** Returns true when a transport has been registered. */
export function isHelpTransportConfigured(): boolean {
  return _transport !== null;
}

export class HelpClientNotConfiguredError extends Error {
  override readonly name = 'HelpClientNotConfiguredError';
}

export class HelpClientConfigurationError extends Error {
  override readonly name = 'HelpClientConfigurationError';
}
